'use client';

import { useEffect } from 'react';
import TagManager from 'react-gtm-module';
import * as Sentry from '@sentry/nextjs';

declare global {
  interface Window {
    clarity: ((...args: unknown[]) => void) & { q?: unknown[][] };
  }
}

const CLARITY_ATTACH_INTERVAL_MS = 1000;
const CLARITY_ATTACH_ATTEMPTS = 15;

/**
 * Clarity's `_clck` (user) and `_clsk` (session) cookies hold their id in the first
 * segment, delimited by `|` or `^` depending on the tag version.
 */
export function clarityCookie(name: string): string | undefined {
  const raw = document.cookie
    .split('; ')
    .find((c) => c.startsWith(`${name}=`))
    ?.split('=')
    .slice(1)
    .join('=');
  if (!raw) return undefined;
  const id = decodeURIComponent(raw).split(/[|^]/)[0];
  return id || undefined;
}

//import '../newrelic-client'
export function ClientTelemetry({
  children,
}: {
  children: React.ReactNode;
}) {
  useEffect(() => {

    if (process.env.NEXT_PUBLIC_GTM_ID) {
      TagManager.initialize({
        gtmId: process.env.NEXT_PUBLIC_GTM_ID,
      });
    }

    // Microsoft Clarity — vendor snippet, kept inline so there's no extra dependency.
    const clarityId = process.env.NEXT_PUBLIC_CLARITY_ID;
    let clarityTimer: number | undefined;
    if (clarityId) {
      if (!window.clarity) {
        window.clarity =
          window.clarity ||
          function (...args: unknown[]) {
            (window.clarity.q = window.clarity.q || []).push(args);
          };
        const script = document.createElement('script');
        script.async = true;
        script.src = `https://www.clarity.ms/tag/${clarityId}`;
        document.head.appendChild(script);
      }

      // Clarity's web SDK has no API for the playback URL (getCurrentSessionUrl is
      // mobile-only), so it's rebuilt from the cookies Clarity writes once the tag
      // boots. Those appear a beat after the script loads, hence the poll.
      const attachClaritySessionToSentry = () => {
        const userId = clarityCookie('_clck');
        const sessionId = clarityCookie('_clsk');
        if (!userId || !sessionId) return false;
        const sessionUrl = `https://clarity.microsoft.com/player/${clarityId}/${userId}/${sessionId}`;
        Sentry.setTag('clarity_session_url', sessionUrl);
        Sentry.setContext('clarity', {
          session_url: sessionUrl,
          project_id: clarityId,
          user_id: userId,
          session_id: sessionId,
        });
        return true;
      };

      if (!attachClaritySessionToSentry()) {
        let attempts = 0;
        clarityTimer = window.setInterval(() => {
          // ponytail: give up after ~15s — no cookies by then means Clarity was
          // blocked or consent was denied, and there is no session to link to.
          if (attachClaritySessionToSentry() || ++attempts >= CLARITY_ATTACH_ATTEMPTS) {
            window.clearInterval(clarityTimer);
          }
        }, CLARITY_ATTACH_INTERVAL_MS);
      }
    }

    
    const handler = function () {

      // logMessage('error', 'Client Side Unhandled Promise Rejection', {
      //   errorMessage: event.reason.message || event.reason,
      //   stack: event.reason.stack || ''
      // });

      // // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // // @ts-ignore
      // window.newrelic.noticeError(event);
    };
    

    window.onunhandledrejection = handler;
    return () => {
      window.onunhandledrejection = null;
      if (clarityTimer) window.clearInterval(clarityTimer);
    };
  }, []);
  return <>{children}</>;
}
